import React from 'react';

export interface MatchTypeSelectorProps {
  initialValue: string;
  disabled: boolean;
  onChange: (value: string) => void;
}

/**
 * Toggle-based match type editor: Play-offs, Bowl Game (+ optional text), Post Season.
 * Combined value is propagated to the parent via onChange.
 */
export default function MatchTypeSelector({
  initialValue,
  disabled,
  onChange,
}: MatchTypeSelectorProps) {
  const normalise = (s: string) => s.toLowerCase().trim();

  const parts = initialValue
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

  const hasInitial = (key: string) =>
    parts.some((p) => normalise(p) === normalise(key));

  const [playoffs, setPlayoffs] = React.useState(
    hasInitial('Playoff') || hasInitial('Play-offs'),
  );
  const [bowlGame, setBowlGame] = React.useState(hasInitial('Bowl Game'));
  const [bowlText, setBowlText] = React.useState(
    parts.find(
      (p) =>
        !['playoff', 'play-offs', 'bowl game', 'post season', 'postseason'].includes(
          normalise(p),
        ),
    ) ?? '',
  );
  const [postSeason, setPostSeason] = React.useState(
    hasInitial('Post Season') ||
      hasInitial('PostSeason') ||
      hasInitial('Playoff') ||
      hasInitial('Play-offs'),
  );

  const buildAndPropagate = React.useCallback(
    (po: boolean, bg: boolean, bt: string, ps: boolean) => {
      const tokens: string[] = [];
      if (po) tokens.push('Play-offs');
      if (bg) {
        tokens.push('Bowl Game');
        if (bt.trim()) tokens.push(bt.trim());
      }
      if (ps) tokens.push('Post Season');
      onChange(tokens.join(', '));
    },
    [onChange],
  );

  const togglePlayoffs = () => {
    const next = !playoffs;
    setPlayoffs(next);
    const nextPs = next ? true : postSeason;
    setPostSeason(nextPs);
    buildAndPropagate(next, bowlGame, bowlText, nextPs);
  };

  const toggleBowlGame = () => {
    const next = !bowlGame;
    setBowlGame(next);
    if (!next) setBowlText('');
    buildAndPropagate(playoffs, next, next ? bowlText : '', postSeason);
  };

  const togglePostSeason = () => {
    const next = !postSeason;
    setPostSeason(next);
    const nextPlayoffs = next ? playoffs : false;
    if (!next) setPlayoffs(false);
    buildAndPropagate(nextPlayoffs, bowlGame, bowlText, next);
  };

  const handleBowlText = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setBowlText(val);
    buildAndPropagate(playoffs, bowlGame, val, postSeason);
  };

  const btnBase =
    'inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-semibold transition-all duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50';
  const btnActive = 'border-sky-500 bg-sky-50 text-sky-700 shadow-sm';
  const btnInactive =
    'border-slate-200 bg-white text-slate-500 hover:border-slate-300 hover:text-slate-700';

  return (
    <div className="flex min-w-[260px] flex-col gap-2">
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          disabled={disabled}
          onClick={togglePlayoffs}
          className={`${btnBase} ${playoffs ? btnActive : btnInactive}`}
          aria-pressed={playoffs}
        >
          {playoffs && (
            <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 14 14" fill="none">
              <path
                d="M2 7l3.5 3.5L12 3"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
          Play-offs
        </button>

        <button
          type="button"
          disabled={disabled}
          onClick={toggleBowlGame}
          className={`${btnBase} ${bowlGame ? btnActive : btnInactive}`}
          aria-pressed={bowlGame}
        >
          {bowlGame && (
            <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 14 14" fill="none">
              <path
                d="M2 7l3.5 3.5L12 3"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
          Bowl Game
        </button>

        <button
          type="button"
          disabled={disabled}
          onClick={togglePostSeason}
          className={`${btnBase} ${postSeason ? btnActive : btnInactive}`}
          aria-pressed={postSeason}
        >
          {postSeason && (
            <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 14 14" fill="none">
              <path
                d="M2 7l3.5 3.5L12 3"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
          Post Season
        </button>
      </div>

      {bowlGame && (
        <input
          type="text"
          value={bowlText}
          onChange={handleBowlText}
          disabled={disabled}
          placeholder="Enter bowl game type…"
          className="w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs text-slate-700 placeholder-slate-400 shadow-sm focus:border-sky-400 focus:outline-none focus:ring-1 focus:ring-sky-300 disabled:opacity-50"
        />
      )}
    </div>
  );
}
